import { useEffect, useState } from "react";
import i18n from "../../i18n";

export default function useCreatePostFormResources({
  enabled = true,
  includeGearData = true,
  includeHookBrands = true,
  includeHookSizes = true,
  includeBaitData = true,
  includeFishingMethods = true,
  includeLureSizes = true,
  includeFishList = true,
} = {}) {
  const [rodDatabase, setRodDatabase] = useState([]);
  const [reelDatabase, setReelDatabase] = useState([]);
  const [braidDatabase, setBraidDatabase] = useState([]);
  const [leaderDatabase, setLeaderDatabase] = useState([]);
  const [lureDatabase, setLureDatabase] = useState([]);
  const [hookBrandDatabase, setHookBrandDatabase] = useState([]);
  const [, setHookSizeOptions] = useState([]);
  const [baitDatabase, setBaitDatabase] = useState([]);
  const [liveBaitDatabase, setLiveBaitDatabase] = useState([]);
  const [deadBaitDatabase, setDeadBaitDatabase] = useState([]);
  const [fishingMethods, setFishingMethods] = useState({});
  const [fishList, setFishList] = useState([]);
  const [lureSizeOptions, setLureSizeOptions] = useState([]);
  const [combinedHookOptions, setCombinedHookOptions] = useState([]);

  useEffect(() => {
    if (!enabled || !includeGearData) return undefined;
    let isMounted = true;

    async function loadGearData() {
      try {
        const [rods, reels, braids, leaders, lures] = await Promise.all([
          fetch("/JSON/rod-data.json").then((response) => response.json()),
          fetch("/JSON/reel-data.json").then((response) => response.json()),
          fetch("/JSON/braid-data.json").then((response) => response.json()),
          fetch("/JSON/leader-data.json").then((response) => response.json()),
          fetch("/JSON/lure-data.json").then((response) => response.json()),
        ]);

        if (!isMounted) return;
        setRodDatabase(Array.isArray(rods) ? rods : []);
        setReelDatabase(Array.isArray(reels) ? reels : []);
        setBraidDatabase(braids);
        setLeaderDatabase(leaders);
        setLureDatabase(
          Array.isArray(lures)
            ? lures
            : Array.isArray(lures?.items)
              ? lures.items
              : []
        );
      } catch (error) {
        console.error("Error loading gear data:", error);
      }
    }

    loadGearData();
    return () => {
      isMounted = false;
    };
  }, [enabled, includeGearData]);

  useEffect(() => {
    if (!enabled || !includeHookBrands) return undefined;
    let isMounted = true;

    async function loadHookBrandData() {
      try {
        const brands = await fetch("/JSON/hook-brand-data.json").then((response) =>
          response.json()
        );

        if (!isMounted) return;
        setHookBrandDatabase(brands);
      } catch (error) {
        console.error("Error loading hook brand data:", error);
      }
    }

    loadHookBrandData();
    return () => {
      isMounted = false;
    };
  }, [enabled, includeHookBrands]);

  useEffect(() => {
    if (!enabled || !includeHookSizes) return undefined;
    let isMounted = true;

    async function loadHookSizeData() {
      try {
        const sizes = await fetch("/JSON/hook-size-data.json").then((response) =>
          response.json()
        );

        if (!isMounted) return;

        const hookSizes = sizes.sizes || {};
        setHookSizeOptions(hookSizes);

        const single = hookSizes.single || [];
        const treble = hookSizes.treble || [];

        setCombinedHookOptions([
          ...single.map((size) => ({
            value: `single|${size}`,
            label: i18n.t("hook_single_size_label", {
              defaultValue: "Single Hook - Size {{size}}",
              size,
            }),
          })),
          ...treble.map((size) => ({
            value: `treble|${size}`,
            label: i18n.t("hook_treble_size_label", {
              defaultValue: "Treble Hook - Size {{size}}",
              size,
            }),
          })),
        ]);
      } catch (error) {
        console.error("Error loading hook size data:", error);
      }
    }

    loadHookSizeData();
    return () => {
      isMounted = false;
    };
  }, [enabled, includeHookSizes]);

  useEffect(() => {
    if (!enabled || !includeBaitData) return undefined;
    let isMounted = true;

    async function loadBaitData() {
      try {
        const [generalBait, liveBait, deadBait] = await Promise.all([
          fetch("/JSON/bait-data.json").then((response) => response.json()),
          fetch("/JSON/live-bait-data.json").then((response) => response.json()),
          fetch("/JSON/dead-bait-data.json").then((response) => response.json()),
        ]);

        if (!isMounted) return;
        setBaitDatabase(generalBait);
        setLiveBaitDatabase(liveBait);
        setDeadBaitDatabase(deadBait);
      } catch (error) {
        console.error("Failed to load bait data:", error);
      }
    }

    loadBaitData();
    return () => {
      isMounted = false;
    };
  }, [enabled, includeBaitData]);

  useEffect(() => {
    if (!enabled || !includeFishingMethods) return undefined;
    let isMounted = true;

    fetch(`${process.env.PUBLIC_URL}/JSON/fishingMethods.json`)
      .then((response) => response.json())
      .then((data) => {
        if (isMounted) setFishingMethods(data);
      })
      .catch((error) => console.error("Failed to load fishingMethods:", error));

    return () => {
      isMounted = false;
    };
  }, [enabled, includeFishingMethods]);

  useEffect(() => {
    if (!enabled || !includeLureSizes) return undefined;
    let isMounted = true;

    fetch("/JSON/lure-size-data.json")
      .then((response) => response.json())
      .then((data) => {
        if (isMounted && data?.sizes) {
          setLureSizeOptions(data.sizes);
        }
      })
      .catch((error) => console.error("Failed to load lure sizes:", error));

    return () => {
      isMounted = false;
    };
  }, [enabled, includeLureSizes]);

  useEffect(() => {
    if (!enabled || !includeFishList) return undefined;
    let isMounted = true;

    fetch("/JSON/fish-data.json")
      .then((response) => response.json())
      .then((data) => {
        if (isMounted) setFishList(data);
      })
      .catch((error) => console.error("Failed to load fish data:", error));

    return () => {
      isMounted = false;
    };
  }, [enabled, includeFishList]);

  return {
    rodDatabase,
    reelDatabase,
    braidDatabase,
    leaderDatabase,
    lureDatabase,
    hookBrandDatabase,
    baitDatabase,
    liveBaitDatabase,
    deadBaitDatabase,
    fishingMethods,
    fishList,
    lureSizeOptions,
    combinedHookOptions,
  };
}
